import { Checkbox, DatePicker, Form, Input, message, Radio, Select, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import cryptoRandomString from 'crypto-random-string';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { TField } from '@/common/basis/types/tfield';
import { TBookingMeeting } from '@/common/basis/types/database/tbooking-meeting';
import { encrypt } from '@/common/utils/crypto-js';
import { TCryptoJS } from '@/common/basis/types/tcrypto-js';
import { TStore } from '@/common/basis/types/tstore';
import { getAutoMuteOptions, getRepeatOptions, getAnonymousOptions } from '@/common/utils/select-options';
import { useMeetingBookingType } from '@/common/hooks/meeting-booking-type';
import { useMeetingBrightPassword } from '@/common/hooks/meeting-bright-password';

function App(props: { formData: TBookingMeeting.Data; setFormData: (formData: TBookingMeeting.Data) => void }) {
  const formData = props.formData;
  const setFormData = props.setFormData;
  const { isBookingLive } = useMeetingBookingType();
  const currentUserName = useSelector((state: TStore.State) => state.person.user?.userName);
  const now = dayjs();
  const { t } = useTranslation();
  const roomName = useMemo(
    () =>
      formData?.roomName ??
      (isBookingLive
        ? t('initiateMeetingLive', { userName: currentUserName })
        : t('initiateMeeting', { userName: currentUserName })),
    [formData?.roomName, isBookingLive, currentUserName],
  );
  const startTime = useMemo(() => formData?.startTime ?? 0, [formData?.startTime]);
  const stopTime = useMemo(() => formData?.stopTime, [formData?.stopTime]);
  const duration = useMemo(() => formData?.duration ?? 0, [formData?.duration]);
  const endTime = useMemo(() => startTime + duration, [startTime, duration]);
  const autoMute = useMemo(() => formData?.autoMute, [formData?.autoMute]);
  const readyMode = useMemo(() => formData?.readyMode, [formData?.readyMode]);
  const isReadyModeOn = useMemo(() => readyMode === TField.READY_MODE_ON, [readyMode]);
  const allowAnonymous = useMemo(() => formData?.allowAnonymous, [formData?.allowAnonymous]);
  const [form] = Form.useForm();
  const repeatType = useMemo(() => formData?.repeatType, [formData?.repeatType]);
  const password = useMemo(() => formData?.password, [formData?.password]);
  const nonce = useMemo(() => formData?.nonce, [formData?.nonce]);
  const { brightPassword } = useMeetingBrightPassword(password, nonce);
  const isRepeatMeeting = useMemo(() => repeatType && repeatType !== TField.MEET_REPEAT_TYPE_NONE, [repeatType]);
  const [messageApi, contextHolder] = message.useMessage({ top: 60 });

  const [startDate, setStartDate] = useState<Dayjs>();
  const [endDate, setEndDate] = useState<Dayjs>();
  const [stopDate, setStopDate] = useState<Dayjs>();
  const [isCutoffTime, setIsCutoffTime] = useState(false); // 是否设置截止时间
  const anonymousControlPermission = useSelector(
    (state: TStore.State) => state.whole.capability?.anonymousControlPermission,
  );

  useEffect(() => {
    form.resetFields(); // 优先重置表单内容
    form.setFieldsValue({ roomName });

    setStartDate(dayjs(startTime));
    setEndDate(dayjs(endTime));

    if (isRepeatMeeting) {
      if (stopTime) {
        setStopDate(dayjs(stopTime));
        setIsCutoffTime(true);
      } else {
        setIsCutoffTime(false);
      }
    } else {
      setIsCutoffTime(false);
      setFormData({ ...formData, stopTime: 0 });
    }

    if (brightPassword) {
      form.setFieldsValue({ pwd: brightPassword });
    }
    if (isBookingLive) {
      setFormData({ ...formData, readyMode });
    }
  }, []);

  return (
    <>
      <Form className='pt-6' form={form} labelCol={{ flex: '110px' }}>
        <Form.Item label={t('meetingTheme')} name='roomName'>
          <Input
            allowClear
            maxLength={100}
            onChange={(evt) => setFormData({ ...formData, roomName: evt.currentTarget.value })}
            placeholder={t('enterMeetingTheme')}
          />
        </Form.Item>
        <Form.Item label={t('meetingTime')}>
          <DatePicker
            allowClear={false}
            format='YYYY-MM-DD HH:mm'
            maxDate={startDate?.get('date') === endDate?.get('date') ? undefined : endDate}
            minDate={now}
            minuteStep={5}
            onChange={(value) => {
              const startCurrentTime = value.valueOf();

              if (startCurrentTime < now.valueOf()) {
                messageApi.destroy();
                messageApi.error(t('startThanCurrent'));

                return;
              }
              setFormData({ ...formData, startTime: startCurrentTime, duration: 60 * 60 * 1000 });
              setStartDate(value);
              setEndDate(value.add(1, 'hour'));
            }}
            showTime
            value={startDate}
          />
          <span className='mx-1'>~</span>
          <DatePicker
            allowClear={false}
            format='YYYY-MM-DD HH:mm'
            minDate={startDate}
            minuteStep={5}
            onChange={(value) => {
              if (value.valueOf() <= (startDate?.valueOf() ?? 0)) {
                messageApi.destroy();
                messageApi.error(
                  value.valueOf() === (startDate?.valueOf() ?? 0) ? t('selectDurationAgain') : t('endThanStart'),
                );

                return;
              }
              setFormData({
                ...formData,
                duration: (value.valueOf() / 1000) * 1000 - ((startDate?.valueOf() ?? 0) / 1000) * 1000,
              });
              setEndDate(value);
            }}
            showTime
            value={endDate}
          />
        </Form.Item>
        <Form.Item label={t('repeatFrequency')}>
          <Select
            onChange={(value) => {
              if (value === TField.MEET_REPEAT_TYPE_NONE) {
                setIsCutoffTime(false);
                setFormData({ ...formData, stopTime: 0, repeatType: value });
              } else {
                setFormData({ ...formData, repeatType: value });
              }
            }}
            options={getRepeatOptions()}
            value={repeatType}
          />
        </Form.Item>
        {isRepeatMeeting ? (
          <Form.Item label={t('repeatEnd')}>
            <Radio
              checked={!isCutoffTime}
              onChange={() => {
                setIsCutoffTime(false);
                setFormData({ ...formData, stopTime: 0 });
                setStopDate(undefined);
              }}
            >
              {t('neverEnd')}
            </Radio>
            <Radio
              checked={isCutoffTime}
              onChange={() => {
                setIsCutoffTime(true);
                setFormData({ ...formData, stopTime: stopDate?.valueOf() ?? now.add(1, 'year').valueOf() });
                setStopDate(stopDate || now.add(1, 'year'));
              }}
            >
              {t('cutoffTime')}
            </Radio>
            <DatePicker
              allowClear={false}
              disabled={!isCutoffTime}
              format='YYYY-MM-DD'
              minDate={endDate}
              onChange={(value) => {
                if (value.valueOf() < (endDate?.valueOf() ?? 0)) {
                  messageApi.destroy();
                  messageApi.error(t('deadlineThanEnd'));

                  return;
                }
                setFormData({ ...formData, stopTime: value.add(24, 'hour').valueOf() });
                setStopDate(value);
              }}
              rootClassName='no-drag'
              value={stopDate}
            />
          </Form.Item>
        ) : (
          <></>
        )}
        <Form.Item
          label={
            <div className='flex items-center justify-end'>
              <div className='mr-1'>{t('joinPassword')}</div>
              <Tooltip styles={{ body: { marginLeft: 20 } }} title={t('passwordTips')}>
                <ExclamationCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />
              </Tooltip>
            </div>
          }
          name='pwd'
        >
          <Input
            maxLength={6}
            onChange={(evt) => {
              const pwd = evt.currentTarget.value.replace(/[\D]/gi, '');

              form.setFieldsValue({ pwd });
              setFormData({
                ...formData,
                password: encrypt({
                  str: pwd,
                  key: nonce ?? cryptoRandomString({ length: 16 }),
                  iv: TCryptoJS.MEET_CLIENT_IV,
                }),
                nonce,
              });
            }}
            placeholder={t('passwordFormat')}
          />
        </Form.Item>
        <Form.Item label={t('muteJoinMeeting')}>
          <Select
            onChange={(value) => setFormData({ ...formData, autoMute: value })}
            options={getAutoMuteOptions()}
            value={autoMute}
          />
        </Form.Item>
        {anonymousControlPermission ? (
          <Form.Item
            label={
              <div className='flex items-center justify-center'>
                <div className='mr-1'>{t('anonymous')}</div>
                <Tooltip styles={{ body: { marginLeft: 20 } }} title={t('anonymousTips')}>
                  <ExclamationCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />
                </Tooltip>
              </div>
            }
          >
            <Select
              onChange={(value) => setFormData({ ...formData, allowAnonymous: value })}
              options={getAnonymousOptions()}
              value={allowAnonymous}
            />
          </Form.Item>
        ) : (
          <></>
        )}
        {isBookingLive ? (
          <Form.Item label={t('meetingManage')}>
            <Checkbox
              checked={isReadyModeOn}
              onChange={() => {
                setFormData({
                  ...formData,
                  readyMode: isReadyModeOn ? TField.READY_MODE_OFF : TField.READY_MODE_ON,
                });
              }}
            >
              {t('prepareMode')}
            </Checkbox>
            <Tooltip title={t('meetingManageDesc')}>
              <ExclamationCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />
            </Tooltip>
          </Form.Item>
        ) : (
          <></>
        )}
      </Form>
      {contextHolder}
    </>
  );
}

export default App;
